import { Button } from "@repo/ui/components/Base/Button";
import { PageHeader } from "@/components/page";
import { Link, useNavigate } from "react-router-dom";
import { Spinner } from "@repo/ui/components/Base/Spinner";
import { useEffect, useState } from "react";
import {
  setLastTeamId,
  useUserStore,
  userHasTeamMinRole,
} from "@/stores/userStore";
import { PlusCircleIcon } from "lucide-react";
import type { Team, Tenant } from "@repo/types";
import { TermsAndConditionsDialog } from "@/components/termsAndConditions/TermsAndConditionsDialog";
import { Tooltip } from "@repo/ui/components/Base/Tooltip";
import { apiService } from "@/services/apiService";
import { useGetTermsAndConditions } from "@/hooks/queryHooks/configHooks";
import { useTenantList } from "@/hooks/queryHooks/tenantHooks";
import { teamRepo } from "@/repositories/teamRepo";
import { firstValueFrom } from "rxjs";

interface DashboardTenantBlockProps {
  tenant: Tenant;
}

const DashboardTenantBlock = ({ tenant }: DashboardTenantBlockProps) => {
  const [teams, setTeams] = useState<Team[]>();
  const user = useUserStore((s) => s.user);
  const showFullOrganization =
    !!user &&
    (tenant.viewUsers.includes(user.id) ||
      tenant.submitUsers.includes(user.id) ||
      tenant.editUsers.includes(user.id) ||
      tenant.adminUsers.includes(user.id));

  useEffect(() => {
    let cancel = false;
    setTeams(undefined);

    if (user) {
      (async () => {
        try {
          await apiService.v1.accessTenant(tenant.id);
          if (cancel) return;

          const teams = await firstValueFrom(teamRepo.list(tenant.id));
          if (cancel) return;

          setTeams(
            teams
              .filter((team) =>
                userHasTeamMinRole(user.id, team, tenant, "view")
              )
              .sort(
                (a, b) =>
                  a.name.localeCompare(b.name) || a.id.localeCompare(b.id)
              )
          );
        } catch {
          if (!cancel) {
            setTeams([]);
          }
        }
      })();
    } else {
      setTeams([]);
    }

    return () => {
      cancel = true;
    };
  }, [tenant, user]);

  return (
    <div
      key={tenant.id}
      onClick={() => console.log(tenant.id)}
      className="flex flex-col border-slate-800 border shadow-md px-4 py-3 rounded-md overflow-hidden"
    >
      <div className="text-2xl">{tenant.name}</div>
      <div className="my-4">Domain: {`${tenant.friendlyId}`}</div>
      <div className="mb-1">Select a Team:</div>
      <div className="mb-2 flex flex-col">
        {!teams ? (
          <Button
            type="button"
            disabled
            className="mb-2 w-full h-8 py-0 px-2 justify-start"
          >
            Loading Teams...
          </Button>
        ) : teams.length > 0 ? (
          teams.map((team) => (
            <Link
              key={team.id}
              onClick={() => setLastTeamId(tenant.id, team.id)}
              to={`/app/tenant/${tenant.id}`}
              className="mb-2"
            >
              <Button
                type="button"
                className="w-full h-8 py-0 px-2 justify-start"
              >
                {team.name} &raquo;
              </Button>
            </Link>
          ))
        ) : (
          <Button
            type="button"
            disabled
            className="mb-2 w-full h-8 py-0 px-2 justify-start"
          >
            No Teams Available
          </Button>
        )}
      </div>
      <div className="grow" />
      {showFullOrganization && (
        <Link
          onClick={() => setLastTeamId(tenant.id, "org")}
          to={`/app/tenant/${tenant.id}`}
        >
          Full Organization &raquo;
        </Link>
      )}
    </div>
  );
};

export const DashboardPage = () => {
  const { data: tenants } = useTenantList();
  const user = useUserStore((s) => s.user);
  const { data: termsData } = useGetTermsAndConditions();

  const navigate = useNavigate();

  useEffect(() => {
    if (tenants && tenants.length === 1) {
      const path = `/app/tenant/${tenants[0].friendlyId}`;
      // WARNING: navigate() re-renders current component before navigating.
      // useEffect must have dependencies to avoid infinite re-renders.
      navigate(path);
    }
  }, [tenants, navigate]);

  const [termsDialogOpen, setTermsDialogOpen] = useState(false);

  useEffect(() => {
    if (!user || !termsData) {
      setTermsDialogOpen(false);
      return;
    }
    if (
      user &&
      (!user.termsAndConditionsAccepted ||
        user.termsAndConditionsAcceptedVersion !== termsData.version)
    ) {
      setTermsDialogOpen(true);
    } else {
      setTermsDialogOpen(false);
    }
  }, [user, termsData]);

  if (!tenants || !user || !termsData)
    return (
      <div className="m-auto">
        <Spinner size="lg" />
      </div>
    );

  const handleGetStarted = () => {
    navigate(`/setup/firsttime`);
  };

  // don't flash an unreachable screen when user is only in one tenant
  if (!tenants || tenants.length === 1) return null;

  return (
    <div className="flex flex-col flex-grow">
      <TermsAndConditionsDialog
        user={user}
        open={termsDialogOpen}
        setOpen={setTermsDialogOpen}
      />
      <PageHeader title="Home">
        <Tooltip label="Create another organization" asChild>
          <Link
            to="/setup/firsttime?stepId=1"
            className="flex flex-row gap-2 p-2 rounded-lg hover:scale-110 duration-100 shadow-2xl"
          >
            <PlusCircleIcon />
          </Link>
        </Tooltip>
      </PageHeader>
      <div className="grid lg:grid-cols-3 md:grid-cols-2 grid-cols-1 items-stretch gap-2 py-2 mt-4">
        {tenants?.map((tenant) => (
          <DashboardTenantBlock key={tenant.id} tenant={tenant} />
        ))}
      </div>
      {tenants?.length === 0 && (
        <div className="flex flex-col flex-grow">
          <div className="mx-auto mt-12 max-w-screen-lg w-full bg-form p-4 rounded-lg ">
            <div className="flex flex-col gap-4">
              <h1>
                It looks like you don't have any organizations that you belong
                to yet. Click "Get Started" below to start creating your first
                organization! If you believe this is a mistake, contact support
                or your organization admin.
              </h1>
              <div className="flex flex-row w-full justify-end">
                <Button onClick={handleGetStarted}>Get Started!</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
